import { useState } from 'react';

export interface DesignEvalSelectOption {
  value: string;
  label: string;
}

export interface DesignEvalSelectProps {
  /** 選択肢 (新しい順で渡す) */
  options: DesignEvalSelectOption[];
  /** 選択中の value */
  value: string;
  /** 選択変更 */
  onChange: (next: string) => void;
  /** aria-label (用途の説明) */
  ariaLabel: string;
}

/**
 * デザイン評価カードの期間ドロップダウン (月 / FY 兼用)。`PeriodSelect` と同じ見た目だが、
 * 選択肢を呼び出し側 (系列の存在月 / FY) から受け取る汎用版。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン評価カードの期間 (月 / FY) ドロップダウン
 * @graph-connects none
 */
export function DesignEvalSelect({ options, value, onChange, ariaLabel }: DesignEvalSelectProps) {
  const [open, setOpen] = useState(false);
  const currentLabel = options.find((o) => o.value === value)?.label ?? value;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 py-1 text-xs font-semibold text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)] transition-colors"
      >
        {currentLabel}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setOpen(false);
            }}
            aria-hidden="true"
          />
          <ul
            role="listbox"
            className="absolute right-0 z-20 mt-1 max-h-[176px] w-44 overflow-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] py-1 shadow-lg"
          >
            {options.map((o) => {
              const selected = o.value === value;
              return (
                <li key={o.value} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                    className={`block w-full px-3 py-1.5 text-left text-xs transition-colors ${
                      selected
                        ? 'bg-[var(--color-bg-hover)] text-[var(--color-text-primary)] font-semibold'
                        : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                    }`}
                  >
                    {o.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
